import type { UnitInterface } from 'store/units/types';
import { UnitNames, UnitTypes } from 'unitData/types';
import { supportsRiskAssessment as supportsRiskAssessmentClassification } from 'util/unitTypeClassification';
import type { ReportTableValues } from './reportKeys';
import k from './reportKeys';
import type { ReportRow } from './types';

export const getTypeText = (type: string): string => {
  const found = UnitNames.find((u) => u.type === type);
  return found?.name2 ?? found?.name ?? type;
};

export type DeckOptions = {
  unit?: '%' | 'mm' | 'm²';
  title?: ReportTableValues;
  decimals?: 0 | 1 | 2;
  relieving?: boolean[]; // Suboptimal solution to relieving decks
};

export const getRelieving = <T>(
  decks: Record<string, T>,
  relieving: boolean[],
): ReportRow => {
  return [
    k.relievingDeck,
    ...Object.values(decks).map((deck, deckIndex) => {
      if (relieving[deckIndex]) {
        return 'Yes';
      }
      return '-';
    }),
  ];
};

export const getDeckValues =
  <T>(decks: Record<string, T>) =>
  (key: keyof T, options?: DeckOptions): ReportRow => {
    return [
      (options?.title ?? key) as ReportTableValues,
      ...(Object.values(decks).map((deck, deckIndex) => {
        if (options?.relieving?.[deckIndex]) {
          return '-';
        }

        return typeof deck[key] === 'number'
          ? `${round(deck[key], options?.decimals ?? 0)} ${options?.unit ?? ''}`
          : deck[key];
      }) as string[]),
    ];
  };

export type SplitDeckOptions = {
  title: ReportTableValues;
  units: ('%' | 'mm')[];
  commaSeparated?: boolean;
  decimals?: 0 | 1 | 2;
  relieving?: boolean[];
};

export const getDeckSplitValue =
  <T>(decks: Record<string, T>) =>
  (keys: (keyof T)[], options: SplitDeckOptions): ReportRow => {
    return [
      options.title,
      ...Object.values(decks).map((deck, deckIndex) => {
        if (options.relieving?.[deckIndex]) {
          return '-';
        }
        return keys
          .map(
            (k, i) =>
              `${round(deck[k] as unknown as number, options.decimals ?? 0)} ${
                options.units[i]
              }`,
          )
          .join(options?.commaSeparated ? ', \r\n' : ' \r\n');
      }),
    ];
  };

export const getDeckHeaders = (decks: Record<string, unknown>) =>
  Object.keys(decks).map((d, i) => `Deck ${i + 1}`);

export const percent = (val: number) => `${val} %`;
export const mm = (val: number) => `${val} mm`;
export const boolFormatted = (val: number | boolean) =>
  val ? 'true' : 'false';
export const round = (value: number, decimals: 0 | 1 | 2 = 2) => {
  const div = decimals === 0 ? 1 : decimals === 1 ? 10 : 100;
  return Math.round((value + Number.EPSILON) * div) / div;
};

type UnitWithClosedCircuit = {
  closedCircuitFlowRate: number;
  closedCircuitLowerFractionSize: number;
  closedCircuitUpperFractionSize: number;
};

export const hasClosedCircuit = (info: UnitWithClosedCircuit) => {
  return [
    info.closedCircuitFlowRate,
    info.closedCircuitLowerFractionSize,
    info.closedCircuitUpperFractionSize,
  ].every((val) => val !== -1);
};

export const getReturnToCrusher = (
  closedCircuitFlowRate: number,
  closedCircuitLowerFractionSize: number,
  closedCircuitUpperFractionSize: number,
): ReportRow => {
  if (
    [
      closedCircuitFlowRate,
      closedCircuitLowerFractionSize,
      closedCircuitUpperFractionSize,
    ].every((v) => v === -1)
  )
    return ['Return flow to crusher', 'N/A'];
  return [
    'Return flow to crusher',
    round(closedCircuitFlowRate, 0) +
      ' mtph, ' +
      closedCircuitLowerFractionSize +
      ' - ' +
      closedCircuitUpperFractionSize +
      ' mm',
  ];
};

export const FREE_FALL_SCREEN_SS = 'Freefall screen SS';
export const FREE_FALL_SCREEN_SF = 'Freefall screen SF';

export const getConventionalScreenTitle = (screen: number) =>
  `Conventional screen ${screen}`;

export const hasReport = (unit: UnitInterface) => {
  return [
    UnitTypes.FEED,
    UnitTypes.SPLIT,
    UnitTypes.FREE_TEXT_BOX,
    UnitTypes.PRINT_WINDOW,
    UnitTypes.ADD,
    UnitTypes.GENERIC_CRUSHER,
    UnitTypes.GENERIC_SCREEN,
    UnitTypes.STORAGE,
    UnitTypes.STORAGE_FEEDER,
    UnitTypes.STORAGE_FEEDER_2_OUTPUTS,
    UnitTypes.BREAKER,
  ].every((type) => type !== unit.type);
};

export const hasFlowProperties = (unitType: UnitTypes) => {
  return ![UnitTypes.BREAKER, UnitTypes.FREE_TEXT_BOX].includes(unitType);
};

export const supportsChamberAnalysis = (unitType: UnitTypes) => {
  return [UnitTypes.CONE_CRUSHER_CH, UnitTypes.CONE_CRUSHER_CS].includes(
    unitType,
  );
};

export const supportsRiskAssessment = (unitType: UnitTypes) => {
  return supportsRiskAssessmentClassification(unitType);
};
